import { diffRegistries } from '@braidlabs/registry';
import type { FragmentManifest } from '@braidlabs/gateway';

/**
 * Where a fragment's newest divergence lives.
 *
 * The OSS editor models **two** places a manifest can be: a browser-held draft and a published
 * snapshot. Its own doc comment is explicit that "drafts are deliberately not server state" —
 * which is true of the OSS gateway and false here. Braid Cloud stores per-fragment `FRAG` draft
 * items, so a manifest in this product can be in three places at once, holding three different
 * values: what this browser is editing, what the control plane has saved, and what is serving
 * traffic.
 *
 * Collapsing any two of those is how an operator publishes something they did not mean to. The
 * dangerous case is `saved` — a colleague's edit, sitting on the control plane, that this browser
 * never made and would sweep into the next publish without ever having displayed it.
 */
export type RegistryState =
  /** All three agree. Published, saved, and untouched here. */
  | 'live'
  /** This browser has edits that are not on the control plane. Nobody else can see them. */
  | 'unsaved'
  /** Saved as a `FRAG` draft and differing from what is published. Composes nothing yet. */
  | 'saved'
  /** Drafted somewhere and never published at all. */
  | 'staged'
  /** Published and serving, but no longer present in either draft. Publishing withdraws it. */
  | 'removed';

export interface RegistryRow {
  id: string;
  state: RegistryState;
  /** The manifest to show — the newest of the three that exists. */
  manifest: FragmentManifest;
  /** Whether each source holds this fragment at all. */
  present: { local: boolean; saved: boolean; published: boolean };
  /**
   * True when the control plane holds a version this browser has not seen.
   *
   * Separate from `state` because it can be true of a row in *any* state, and because it is the
   * one condition on this page that is somebody else's edit rather than your own.
   */
  divergedFromSaved: boolean;
}

/** Single-manifest equality, borrowed from the same diff the snapshot id is derived through. */
function sameManifest(a: FragmentManifest | undefined, b: FragmentManifest | undefined): boolean {
  if (!a || !b) return a === b;
  return diffRegistries([a], [b]).identical;
}

/**
 * Reconciles the three places a manifest lives into one row per fragment.
 *
 * Ordered published-first, the way `Fragments.tsx` orders its table and for the same reason: what
 * is serving traffic is the thing an operator is reasoning about, and a list ordered by draft
 * would bury a `removed` row that is still live for every visitor.
 */
export function buildRegistryRows(
  local: readonly FragmentManifest[],
  saved: readonly FragmentManifest[],
  published: readonly FragmentManifest[],
): RegistryRow[] {
  const localById = new Map(local.map((manifest) => [manifest.id, manifest]));
  const savedById = new Map(saved.map((manifest) => [manifest.id, manifest]));
  const publishedById = new Map(published.map((manifest) => [manifest.id, manifest]));

  const ids: string[] = [];
  for (const manifest of published) ids.push(manifest.id);
  for (const manifest of [...local, ...saved]) {
    if (!publishedById.has(manifest.id) && !ids.includes(manifest.id)) ids.push(manifest.id);
  }

  return ids.map((id) => {
    const localManifest = localById.get(id);
    const savedManifest = savedById.get(id);
    const publishedManifest = publishedById.get(id);

    const present = {
      local: Boolean(localManifest),
      saved: Boolean(savedManifest),
      published: Boolean(publishedManifest),
    };
    const divergedFromSaved = present.local && present.saved && !sameManifest(localManifest, savedManifest);

    return {
      id,
      state: stateOf(localManifest, savedManifest, publishedManifest),
      // The newest that exists. A removed row has only a published manifest, and showing it is the
      // point — it is what a visitor is composing right now.
      manifest: (localManifest ?? savedManifest ?? publishedManifest)!,
      present,
      divergedFromSaved,
    };
  });
}

function stateOf(
  local: FragmentManifest | undefined,
  saved: FragmentManifest | undefined,
  published: FragmentManifest | undefined,
): RegistryState {
  if (!local && !saved) return 'removed';
  if (!published) return 'staged';
  // A local edit outranks a saved one: it is the newer of the two and the one about to be published.
  if (local && !sameManifest(local, saved)) return 'unsaved';
  if (!sameManifest(saved ?? local, published)) return 'saved';
  return 'live';
}

/** How each state reads in the table, and what it means for traffic right now. */
export const STATE_COPY: Readonly<Record<RegistryState, { label: string; meaning: string }>> = {
  live: { label: 'Live', meaning: 'Published and serving traffic. Nothing is pending.' },
  unsaved: {
    label: 'Unsaved',
    meaning: 'Edited in this browser only. Closing the tab loses it, and nobody else can see it.',
  },
  saved: {
    label: 'Saved',
    meaning: 'Saved to the control plane and composing nothing until the next publish.',
  },
  staged: { label: 'Staged', meaning: 'Drafted and never published — it composes nothing.' },
  removed: {
    label: 'Removed',
    meaning: 'Still published and serving, but gone from the draft. Publishing withdraws it.',
  },
};
